import { z } from 'zod'
import { CONFIRMACOES, DESTINACOES_VENDA, REGIMES_CONTRAPARTE } from './contraparte'
import { PERFIS_SERVICO } from './empresa'
import { PremissasPlanejamento, type ResultadoPlanejamento } from './planejamento'
import { DESTINACOES, TRATAMENTOS } from './produto'

/**
 * Contrato do motor de simulação (arquitetura §10) — F5, anos 2027–2028.
 * Dinheiro em centavos inteiros; alíquotas e percentuais em ppm (1% = 10.000).
 * O motor não sabe de banco nem de relógio: tudo que ele usa chega aqui.
 */

export const CENARIOS = ['OTIMISTA', 'BASE', 'CONSERVADOR'] as const
export type Cenario = (typeof CENARIOS)[number]
export const ROTULO_CENARIO: Record<Cenario, string> = { OTIMISTA: 'Otimista', BASE: 'Base', CONSERVADOR: 'Conservador' }

export const ANOS_SIMULAVEIS = [2027, 2028, 2029, 2030, 2031, 2032, 2033] as const

const Centavos = z.number().int()
const Ppm = z.number().int().min(0).max(1_000_000)
export const NATUREZAS_VENDA = ['MERCADORIA', 'SERVICO', 'TRANSPORTE_CARGA', 'TRANSPORTE_PASSAGEIROS'] as const
const Competencia = z.string().regex(/^\d{4}-\d{2}$/)

export const TributosObservados = z.object({ icms: Centavos, ipi: Centavos, pis: Centavos, cofins: Centavos, iss: Centavos })
export type TributosObservados = z.infer<typeof TributosObservados>

/** Saídas agregadas por competência × enquadramento (não nota a nota). */
export const LinhaSaida = z.object({
  competencia: Competencia,
  tratamento: z.enum(TRATAMENTOS),
  /** produto com perfil aprovado (ou sugestão aceita); serviço de NFS-e ainda não classificado = false */
  classificado: z.boolean(),
  impostoSeletivo: z.boolean(),
  destinacao: z.enum(DESTINACOES_VENDA),
  valor: Centavos,
  tributos: TributosObservados,
  /** parte do valor com ICMS já retido por ST, isenta ou não tributada (CST 40/41/50/60, CSOSN 300/400/500) */
  valorSemIcmsProprio: Centavos.optional(),
  /** parte do valor com ICMS já recolhido por substituição tributária (CST 60, CSOSN 500): no Simples, sai o ICMS do DAS */
  valorIcmsSt: Centavos.optional(),
  /** pelo modelo da nota: NF-e/NFC-e = mercadoria; CT-e = transporte de carga; CT-e OS = passageiros; demais = serviço */
  natureza: z.enum(NATUREZAS_VENDA).optional(),
  /**
   * Venda ao exterior (bens ou serviços): imune ao IBS e à CBS, com o crédito das compras mantido
   * (LC 214, arts. 8º e 79). No Simples, o DAS perde as parcelas de ICMS e ISS (LC 123, art. 18 § 14)
   * e as de IPI, IBS e CBS (§ 14-A).
   */
  exportacao: z.boolean().optional(),
})
export type LinhaSaida = z.infer<typeof LinhaSaida>

export const LinhaEntrada = z.object({
  competencia: Competencia,
  regimeFornecedor: z.enum(REGIMES_CONTRAPARTE),
  confirmacao: z.enum(CONFIRMACOES),
  natureza: z.enum(['MERCADORIA', 'SERVICO']),
  destinacao: z.enum(DESTINACOES),
  tratamento: z.enum(TRATAMENTOS),
  valor: Centavos,
  tributos: TributosObservados,
  /** crédito de ICMS destacado por fornecedor do Simples (vCredICMSSN) */
  icmsCreditoSimples: Centavos.optional(),
})
export type LinhaEntrada = z.infer<typeof LinhaEntrada>

/** Devoluções e operações fora da conta num mês do ano-base (pelo CFOP do item: `operacaoDoCfop`). */
export const OperacoesMes = z.object({
  competencia: Competencia,
  /** devoluções de venda recebidas: abatem a receita do mês */
  devolucoesVenda: Centavos,
  /** devoluções de compra emitidas: abatem as compras do mês */
  devolucoesCompra: Centavos,
  /** saídas que não são venda (remessa, transferência, bonificação, venda de ativo…): fora do débito */
  foraVendas: Centavos,
  /** entradas que não são compra (retorno, remessa recebida, transferência, bonificação…): fora do crédito */
  foraCompras: Centavos,
})
export type OperacoesMes = z.infer<typeof OperacoesMes>

export const RegimeEmpresaMotor = z.enum(['SIMPLES', 'PRESUMIDO', 'REAL', 'MEI', 'OUTRO'])

export const Dossie = z.object({
  empresa: z.object({
    regime: RegimeEmpresaMotor,
    anexoSimples: z.enum(['I', 'II', 'III', 'IV', 'V']).nullable(),
    areaAtuacao: z.enum(['servico', 'industria', 'comercio', 'misto']).nullable(),
    perfilServico: z.enum(PERFIS_SERVICO).nullable().optional(),
    requisitosPerfil: z.boolean().optional(),
  }),
  /** competências observadas usadas como "ano-base" (até 12, as mais recentes) */
  competencias: z.array(Competencia).max(12),
  saidas: z.array(LinhaSaida),
  entradas: z.array(LinhaEntrada),
  /** ausente em dossiês antigos: nada a abater */
  operacoes: z.array(OperacoesMes).optional(),
  /**
   * Receita bruta de TODAS as vendas da empresa nas mesmas competências — inclusive as que o recorte
   * deixou de fora. Só vem preenchida quando o dossiê é um recorte (o contrato com uma contraparte):
   * a faixa do Simples é da empresa inteira, e sem isto o recorte cairia numa faixa menor e num DAS
   * mais barato do que o real. Quando vem, a RBT12 é sempre anualizada (a faixa é de 12 meses).
   */
  receitaBrutaEmpresa: Centavos.optional(),
})
export type Dossie = z.infer<typeof Dossie>

export const Premissas = z.object({
  /** regime assumido para fornecedor de regime desconhecido (§10.4) */
  regimeFornecedorDesconhecido: z.enum(['REGULAR', 'SN_PURO', 'SN_HIBRIDO']).default('SN_PURO'),
  /** receita de 12 meses presumida do fornecedor do Simples puro, para achar a alíquota efetiva do crédito */
  rbt12FornecedorSimples: z.number().int().positive().max(480_000_000, 'RBT12 acima de R$ 4,8 milhões não é Simples Nacional').default(100_000_000),
  /** menos de 12 meses observados: projeta o ano proporcionalmente (e avisa) */
  anualizar: z.boolean().default(true),
  /** Simples, híbrido: fração do ajuste de preço que vai para o cliente (0 = cliente paga o mesmo; 100% = você mantém o preço sem tributo) */
  repasse: Ppm.default(0),
  /** Simples, híbrido: repasse só nas vendas a empresas que se creditam (sem valor = `repasse`) */
  repasseComCredito: Ppm.optional(),
  /** Simples, híbrido: repasse só nas vendas a consumidor final e empresas sem crédito (sem valor = `repasse`) */
  repasseSemCredito: Ppm.optional(),
  /** Simples, híbrido: venda a empresa de crédito incerto (consumo final, regime indefinido) conta como */
  vendaCreditoIncerto: z.enum(['SEM_CREDITO', 'COM_CREDITO']).default('SEM_CREDITO'),
  /** compras de uso e consumo dão crédito de CBS/IBS (LC 214, art. 47; fora o uso pessoal do art. 57). Ausente = sim */
  creditoUsoConsumo: z.boolean().optional(),
  /** planejamento Simples × Presumido × Real (folha, pró-labore, despesas); ausente = premissas padrão */
  planejamento: PremissasPlanejamento.optional(),
})
export type Premissas = z.infer<typeof Premissas>

export const TRIBUTOS_SIMPLES = ['IRPJ', 'CSLL', 'PIS_COFINS', 'CBS', 'IBS', 'CPP', 'ICMS', 'ISS', 'IPI'] as const
export type TributoSimples = (typeof TRIBUTOS_SIMPLES)[number]

export const FaixaSimples = z.object({
  faixa: z.number().int(),
  ate: Centavos,
  aliquota: Ppm,
  deducao: Centavos,
  partilha: z.record(z.enum(TRIBUTOS_SIMPLES), Ppm),
})
export const TabelaSimples = z.object({
  anexo: z.enum(['I', 'II', 'III', 'IV', 'V']),
  faixas: z.array(FaixaSimples).length(6),
  regrasExcedente: z.array(
    z.object({ faixa: z.number().int(), efetivaAcimaDe: Ppm, issFixo: Ppm, excedente: z.record(z.enum(TRIBUTOS_SIMPLES), Ppm), issFixoIbsParte: Ppm.optional() }),
  ),
  fonte: z.string(),
})
export type TabelaSimples = z.infer<typeof TabelaSimples>

export const EstimativaCbs = z.object({ aliquotaPpm: Ppm, fonte: z.string(), url: z.string().nullable() })

/** Parâmetros de uma versão (gerados em packages/knowledge/parametros/<versao>). */
export const ParametrosMotor = z.object({
  versao: z.string(),
  transicao: z.record(
    z.string(),
    z.object({
      ibsUf: Ppm,
      ibsMun: Ppm,
      /** 2029–2033: IBS como fração da alíquota plena estimada (art. 367, V); ausente em 2027–2028 */
      ibsProporcaoPleno: Ppm.optional(),
      reducaoCbsPp: Ppm,
      pisCofins: z.string(),
      ipi: z.string(),
      /** fração do ICMS/ISS de hoje que ainda existe no ano (arts. 501 e 508) */
      icmsIssFator: Ppm,
    }),
  ),
  fundamentoTransicao: z.string(),
  reducoes: z.record(z.string(), Ppm),
  pisCofinsNaoCumulativo: Ppm,
  simples: z.record(z.string(), z.array(TabelaSimples)),
  estimativasCbs: z.object({
    status: z.enum(['ESTIMATIVA', 'OFICIAL']),
    aviso: z.string(),
    consultadoEm: z.string(),
    anos: z.record(z.string(), z.record(z.enum(CENARIOS), EstimativaCbs)),
  }),
  /** alíquota plena do IBS estimada por cenário (usada de 2029 a 2033) */
  estimativasIbsPleno: z.record(z.enum(CENARIOS), EstimativaCbs).optional(),
  impostoSeletivo: z.object({ situacao: z.string(), url: z.string() }).optional(),
  /** quando o Simples opta pelo IBS/CBS no regime regular (LC 123, art. 13 § 10) */
  opcaoRegimeRegularSimples: z.object({
    semestres: z.array(z.object({ semestre: z.string(), inicio: z.string(), mesOpcao: z.string(), cancelamentoAte: z.string().optional() })),
    fundamento: z.string(),
    permanencia: z.string().optional(),
    fundamentoCancelamento: z.string().optional(),
    urlCancelamento: z.string().optional(),
  }),
  /** IRPJ, CSLL, presunção, CPP e limites para o planejamento de regime (ausente em versões antigas) */
  regimesLucro: z
    .object({
      irpj: z.object({ aliquota: Ppm, adicional: Ppm, limiteAdicionalMensal: z.number().int() }),
      csll: z.object({ aliquota: Ppm }),
      presuncao: z.record(z.string(), z.object({ irpj: Ppm, csll: Ppm })),
      acrescimoLc224: z.object({ percentual: Ppm, limiteAnual: z.number().int(), inicioIrpj: z.string(), inicioCsll: z.string(), situacao: z.string() }),
      pisCofinsCumulativo: Ppm,
      limitePresumido: z.number().int(),
      limiteSimples: z.number().int(),
      sublimiteSimples: z.number().int().optional(),
      cpp: z.object({ patronal: Ppm, rat: z.record(z.string(), Ppm), contribuinteIndividual: Ppm }),
      fatorR: Ppm,
      fundamento: z.string(),
    })
    .optional(),
})
export type ParametrosMotor = z.infer<typeof ParametrosMotor>

export const SimulacaoInput = z.object({
  dossie: Dossie,
  parametros: ParametrosMotor,
  premissas: Premissas,
  anos: z.array(z.number().int().min(2027).max(2033)).min(1),
  versaoKb: z.string(),
  /** instante do cálculo, injetado (o motor não lê relógio) */
  calculadoEm: z.string(),
})
export type SimulacaoInput = z.infer<typeof SimulacaoInput>

export const FATORES_DECOMPOSICAO = [
  'FIM_PIS_COFINS',
  'FIM_IPI',
  'ALIQUOTA_CBS_IBS',
  'REDUCAO_SETORIAL',
  'CREDITO_NOVO',
  'CREDITO_SIMPLES',
  'TABELA_SIMPLES',
  'TRANSICAO_ICMS_ISS',
] as const
export type FatorDecomposicao = (typeof FATORES_DECOMPOSICAO)[number]

export const ROTULO_FATOR: Record<FatorDecomposicao, string> = {
  FIM_PIS_COFINS: 'Fim do PIS/COFINS',
  FIM_IPI: 'Fim do IPI',
  ALIQUOTA_CBS_IBS: 'CBS e IBS sobre as vendas',
  REDUCAO_SETORIAL: 'Reduções da LC 214 nos seus produtos',
  CREDITO_NOVO: 'Crédito de CBS/IBS nas compras',
  CREDITO_SIMPLES: 'Crédito limitado de fornecedores do Simples',
  TABELA_SIMPLES: 'Nova tabela do Simples',
  TRANSICAO_ICMS_ISS: 'Transição do ICMS/ISS',
}

export interface CargaDetalhada {
  total: number
  pisCofins: number
  ipi: number
  icms: number
  iss: number
  cbs: number
  ibs: number
  das: number
  /** créditos que abatem a carga (já descontados no total) */
  creditos: number
}

export interface ResultadoCenario {
  ano: number
  cenario: Cenario
  aliquotas: {
    cbsReferencia: number
    cbs: number
    ibs: number
    status: 'ESTIMATIVA' | 'OFICIAL'
    fonte: string
    url: string | null
    /** 2029–2033: IBS pleno estimado e a fração dele aplicada no ano */
    ibsPleno?: { aliquota: number; proporcao: number; fonte: string; url: string | null } | null
    /** fração do ICMS/ISS de hoje que ainda existe no ano (1.000.000 = inteiro; 0 = extinto) */
    icmsIssFator?: number
  }
  receita: number
  atual: CargaDetalhada
  nova: CargaDetalhada
  variacao: number
  /** variação sobre a receita, em ppm (10.000 = 1 ponto percentual) */
  variacaoSobreReceita: number
  decomposicao: Array<{ fator: FatorDecomposicao; valor: number }>
  porCompetencia: Array<{
    competencia: string
    receita: number
    cargaAtual: number
    cargaNova: number
    debitoCbsIbs: number
    creditoCbsIbs: number
    /** entradas do mês (valor das notas) */
    compras?: number
    debitoCbs?: number
    debitoIbs?: number
    creditoCbs?: number
    creditoIbs?: number
  }>
  /** créditos de CBS/IBS que vêm de fornecedor com regime presumido ou assumido */
  creditoDependePremissa: number
  /** vendas de produtos sujeitos ao Imposto Seletivo (alíquotas ainda não definidas em lei: não quantificado) */
  impostoSeletivo?: { receitaSujeita: number; quantificado: false }
  /** receita de exportação no ano (imune ao IBS e à CBS) */
  receitaExportacao?: number
  /** Simples: CBS e IBS dentro do DAS (o que o cliente PJ poderá creditar) */
  simples: { efetivaAtual: number; efetivaNova: number; rbt12: number; cbsNoDas: number; ibsNoDas: number } | null
}

export interface ResumoOperacoes {
  /** vendas e compras que entraram na conta, antes de abater devoluções */
  vendas: number
  compras: number
  devolucoesVenda: number
  devolucoesCompra: number
  /** o que efetivamente abateu (limitado ao valor do próprio mês) */
  abatidoVendas: number
  abatidoCompras: number
  foraVendas: number
  foraCompras: number
  /** compras de uso e consumo (crédito de CBS/IBS conforme a premissa) */
  usoConsumo: number
}

export interface SimulacaoResultado {
  resultados: ResultadoCenario[]
  /** só empresa do Simples: puro × híbrido por ano, no cenário base (ausente em resultados antigos) */
  hibrido?: ResultadoHibrido[] | null
  /** Simples × Presumido × Real (ausente em resultados antigos ou parâmetros sem regimes de lucro) */
  planejamento?: ResultadoPlanejamento | null
  /** o que as regras de CFOP tiraram ou abateram, no ano-base sem anualizar (ausente em resultados antigos) */
  operacoes?: ResumoOperacoes
  qualidade: {
    meses: number
    fatorAnualizacao: number
    /** % da receita (ppm) em saída sem classificação aprovada */
    receitaSemClassificacao: number
    /** % das compras (ppm) de fornecedor com regime presumido/desconhecido */
    comprasRegimePresumido: number
    avisos: string[]
  }
  proveniencia: { versaoEngine: string; versaoParametros: string; versaoKb: string; calculadoEm: string; statusAliquota: 'ESTIMATIVA' | 'OFICIAL' }
}

// ─── Simples: puro × híbrido (arquitetura §11) ───────────────────────────────

export const GRUPOS_CREDITO = ['COM_CREDITO', 'SEM_CREDITO'] as const
export type GrupoCredito = (typeof GRUPOS_CREDITO)[number]

export interface LadoSimples {
  /** receita sem o IBS/CBS por fora (no puro, a de hoje) */
  receita: number
  das: number
  /** híbrido: IBS/CBS destacados nas vendas (repassados na nota) */
  debitoCbsIbs: number
  /** híbrido: crédito das compras */
  creditoEntradas: number
  /** o que sobra para a empresa depois do DAS e do IBS/CBS líquido */
  resultado: number
}

export const FATORES_SENSIBILIDADE = ['ALIQUOTA', 'MIX', 'FORNECEDORES', 'REPASSE', 'CREDITO_INCERTO'] as const
export type FatorSensibilidade = (typeof FATORES_SENSIBILIDADE)[number]
export const ROTULO_SENSIBILIDADE: Record<FatorSensibilidade, string> = {
  ALIQUOTA: 'Alíquota da CBS',
  MIX: 'Vendas para quem se credita (±10 p.p.)',
  FORNECEDORES: 'Fornecedores do Simples não confirmados',
  REPASSE: 'Repasse do ajuste de preço ao cliente',
  CREDITO_INCERTO: 'Vendas a empresa de crédito incerto',
}

export const POLITICAS_PRECO = ['CLIENTE_PAGA_MESMO', 'EMPRESAS_PAGAM_TRIBUTO', 'MANTEM_PRECO'] as const
export type PoliticaPreco = (typeof POLITICAS_PRECO)[number]
export const ROTULO_POLITICA_PRECO: Record<PoliticaPreco, string> = {
  CLIENTE_PAGA_MESMO: 'Todo cliente paga o mesmo que hoje (preço cai)',
  EMPRESAS_PAGAM_TRIBUTO: 'Empresas: mantém o preço e soma o tributo · consumidor: paga o mesmo',
  MANTEM_PRECO: 'Mantém o preço e soma o tributo para todos',
}

/** Uma venda: o que sai na nota, o que o cliente credita e paga de fato, e o que fica para a empresa. */
export interface ExemploVenda {
  /** preço do produto, sem IBS/CBS por fora */
  preco: number
  /** IBS/CBS destacado por fora (0 no puro) */
  tributo: number
  /** total da nota */
  nota: number
  /** crédito de IBS/CBS que o cliente aproveita (0 para consumidor) */
  creditoCliente: number
  /** custo real do cliente = nota − crédito */
  custoCliente: number
  /** DAS da empresa sobre a venda */
  das: number
  /** o que sobra para a empresa = preço − DAS (antes do crédito das compras) */
  fica: number
}

/** Um jeito de formar o preço no híbrido, com a conta do ano. */
export interface PrecoCaixaCenario {
  /** total das notas do ano (preço + IBS/CBS destacado) */
  nota: number
  preco: number
  tributo: number
  das: number
  /** o que fica para a empresa: preço − DAS + crédito das compras */
  sobra: number
  /** o que a operação custa ao cliente no ano: a nota menos o crédito que ele aproveita */
  custoCliente: number
}

export interface PrecoCaixaGrupo {
  /** total das notas de hoje (puro) */
  receitaHoje: number
  /** o que fica para a empresa hoje, no Simples puro */
  sobraPuro: number
  /** o que a operação custa ao cliente hoje (empresa que credita: menos a CBS/IBS de dentro do DAS) */
  custoClienteHoje: number
  /** alíquota média de IBS/CBS das vendas (ppm) */
  aliquotaMedia: number
  /** crédito das compras considerado nesta conta (no grupo, proporcional à receita) */
  creditoCompras: number
  /** vendendo pelo mesmo valor final de hoje (o cliente paga a mesma nota) */
  mesmaNota: PrecoCaixaCenario
  /** aumento no valor final da nota que repõe a sobra do puro (ppm, negativo = pode baixar) */
  aumento: number
  /** o piso: o menor preço que mantém a mesma sobra de caixa */
  mesmaSobra: PrecoCaixaCenario
  /** o mesmo piso sem contar o crédito das compras (só o efeito do DAS menor e do tributo por fora) */
  aumentoSemCredito: number
  semCreditoCompras: PrecoCaixaCenario
}

/** Um ponto de preço nas vendas a empresas que se creditam, no ano (cenário base). */
export interface PontoPreco {
  /** preço dos produtos, sem IBS/CBS */
  preco: number
  /** IBS/CBS destacado por fora */
  tributo: number
  /** total das notas */
  nota: number
  /** custo real do cliente: a nota menos o IBS/CBS que ele credita (= preço) */
  custoCliente: number
  /** o que fica para a empresa com essas vendas: preço − DAS + crédito das compras rateado */
  sobra: number
}

/**
 * Faixa de preço para o cliente empresa que se credita (ADR 0023): do piso, que mantém a sobra de hoje, ao teto,
 * em que o cliente paga o mesmo que pagaria se a empresa ficasse no puro. Dentro dela os dois lados ganham.
 * Consumidor final e empresas sem crédito ficam na mesma nota de hoje.
 */
export interface ZonaPreco {
  /** notas de hoje das vendas a quem se credita */
  receitaHoje: number
  /** o que essas vendas deixam hoje, no Simples puro */
  sobraPuro: number
  /** custo do cliente se a empresa ficar no puro: credita só a CBS/IBS de dentro do DAS */
  custoClientePuro: number
  /**
   * custo do cliente em 2026, pelo regime dele: no Lucro Real credita o PIS/COFINS não cumulativo sobre a compra
   * de empresa do Simples; no Presumido não credita nada
   */
  custoClienteHoje: { lucroReal: number; presumido: number }
  /** alíquota de PIS/COFINS não cumulativo usada no custo de hoje (ppm, dos parâmetros) */
  pisCofinsNaoCumulativo: number
  /** menor preço que mantém a sobra do puro */
  piso: PontoPreco
  /** preço em que o cliente paga o mesmo que no puro */
  teto: PontoPreco
  /** meio da faixa: os dois lados dividem o ganho */
  sugerido: PontoPreco
  /** false quando o piso fica acima do teto: não há preço em que os dois ganhem (sugerido = teto) */
  existe: boolean
  /** consumidor final e empresas sem crédito, vendendo pela mesma nota de hoje */
  semCredito: { receitaHoje: number; sobraPuro: number; sobraMesmaNota: number } | null
  /** vendas ao exterior: imunes, preço não muda; no híbrido ainda entra o crédito das compras */
  exportacao: { receitaHoje: number; sobraPuro: number; sobraHibrido: number } | null
  /** ganho da empresa no ano contra o puro, em cada ponto (vendas sem crédito na mesma nota) */
  ganhoAnual: { piso: number; sugerido: number; teto: number }
  /** híbrido quando existe preço em que a empresa ganha e nenhum cliente paga mais do que no puro */
  decisao: 'HIBRIDO' | 'PURO'
}

export interface ResultadoHibrido {
  ano: number
  aliquotas: { cbs: number; ibs: number; status: 'ESTIMATIVA' | 'OFICIAL' }
  puro: LadoSimples
  hibrido: LadoSimples
  /** híbrido − puro; positivo favorece o híbrido */
  diferenca: number
  recomendacao: 'HIBRIDO' | 'PURO'
  /** a recomendação não vira em nenhum extremo da sensibilidade */
  estavel: boolean
  mix: {
    /** fração da receita (ppm) vendida a quem se credita */
    atual: number
    /** fração a partir da qual o híbrido compensa (ppm); null quando não há virada entre 0% e 100% */
    pontoVirada: number | null
    /** sem virada: o híbrido ganha (true) ou perde (false) em qualquer mix */
    semViradaHibridoGanha: boolean | null
  }
  porGrupo: Array<{
    grupo: GrupoCredito
    receita: number
    /** alíquota média de IBS/CBS das vendas do grupo (ppm) */
    aliquotaMedia: number
    /** preço sem tributo que deixa o cliente indiferente, como variação sobre o preço de hoje (ppm, com sinal) */
    precoEquilibrio: number
    /** quanto o cliente paga a mais (+) ou a menos (−) no ano, com o repasse escolhido */
    custoCliente: number
    /** quanto essas vendas somam (+) ou tiram (−) do resultado do híbrido contra o puro */
    ganho?: number
  }>
  sensibilidade: Array<{ fator: FatorSensibilidade; rotuloA: string; diferencaA: number; rotuloB: string; diferencaB: number; vira: boolean }>
  /** crédito das compras que depende de regime presumido */
  creditoDependePremissa: number
  /** guias do ano (cenário base): puro = DAS; híbrido = DAS reduzido + CBS e IBS líquidos (negativo = saldo credor) */
  aPagar?: { puro: { das: number; total: number }; hibrido: { das: number; cbs: number; ibs: number; total: number } }
  /** o mesmo mês a mês, sem anualizar (competências reais do ano-base) */
  porCompetencia?: Array<{ competencia: string; vendas: number; compras: number; dasPuro: number; dasHibrido: number; debitoCbs: number; debitoIbs: number; creditoCbs: number; creditoIbs: number }>
  /** crédito das compras no híbrido (parte do ganho que não depende de para quem se vende) */
  creditoCompras?: number
  /** a mesma conta nos cenários otimista e conservador de alíquota */
  faixa?: Record<'otimista' | 'conservador', { cbs: number; ibs: number; diferenca: number; aPagarPuro: number; aPagarHibrido: number }>
  /**
   * Três formas de formar o preço no híbrido, com a conta do ano (cenário base):
   * cliente paga o mesmo (0/0), empresas pagam o tributo e consumidor paga o mesmo (100%/0), preço mantido para todos (100%/100%).
   */
  politicasPreco?: Array<{ politica: PoliticaPreco; repasseComCredito: number; repasseSemCredito: number; diferenca: number; guiasHibrido: number; recomendacao: 'HIBRIDO' | 'PURO' }>
  /** repasse efetivamente usado por grupo (premissas) */
  repasseUsado?: Record<GrupoCredito, number>
  /** uma venda de R$ 10.000,00 (tributação integral) hoje e no híbrido, por tipo de cliente */
  exemploPreco?: Array<{ grupo: GrupoCredito; hoje: ExemploVenda; hibrido: Array<ExemploVenda & { repasse: number }> }>
  /** vender pelo mesmo valor final de hoje e o aumento que mantém a sobra de caixa (cenário base) */
  precoCaixa?: { total: PrecoCaixaGrupo; grupos: Array<PrecoCaixaGrupo & { grupo: GrupoCredito }> }
  /** faixa de preço para quem se credita e a decisão que sai dela (ADR 0023); null sem vendas a quem se credita */
  zona?: ZonaPreco | null
  /** calendário da opção, dos parâmetros usados no cálculo */
  opcao: {
    semestres: Array<{ semestre: string; inicio: string; mesOpcao: string; cancelamentoAte?: string }>
    fundamento: string
    permanencia?: string
    fundamentoCancelamento?: string
    urlCancelamento?: string
  }
}
